import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getMe } from '../../services/authApi';
import {
  SESSION_EVENT,
  getSessionToken,
} from '../../services/syncApi';

/*
 * Who is signed in.
 *
 * GET /auth/me returns the authenticated principal.
 *
 * Authorization supports both:
 *
 * Old authentication:
 *   {
 *     is_human: true,
 *     is_admin: true
 *   }
 *
 * Keycloak authentication:
 *   {
 *     is_human: true,
 *     roles: ["admin"]
 *   }
 *
 * This keeps the frontend compatible while authentication
 * is being migrated to Keycloak.
 */

const SessionContext = createContext({
  principal: null,
  loading: true,
  signedIn: false,
  isAdmin: false,
  hasRole: () => false,
  refresh: () => {},
});

export const useSession = () => useContext(SessionContext);

export const SessionProvider = ({ children }) => {
  const [principal, setPrincipal] = useState(null);
  const [loading, setLoading] = useState(true);

  /*
   * Reload the authenticated user from the backend.
   */
  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      const me = await getMe();

      // Useful while migrating to Keycloak.
      // Do NOT log access tokens here.
      console.log('Authenticated principal:', me);

      setPrincipal(me);
    } catch (error) {
      console.error(
        'Unable to load authenticated principal:',
        error,
      );

      setPrincipal(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /*
   * Load the session when the application starts and refresh it
   * whenever the authentication/session layer reports a change.
   */
  useEffect(() => {
    refresh();

    const onChange = () => {
      refresh();
    };

    window.addEventListener(SESSION_EVENT, onChange);

    return () => {
      window.removeEventListener(SESSION_EVENT, onChange);
    };
  }, [refresh]);

  const value = useMemo(() => {
    /*
     * Normalise roles returned by /auth/me.
     *
     * Expected Keycloak-backed response:
     *
     * {
     *   "is_human": true,
     *   "roles": ["admin", "staff"]
     * }
     */
    const roles = Array.isArray(principal?.roles)
      ? principal.roles
      : [];

    /*
     * Support both authorization models:
     *
     * Old:
     *   principal.is_admin === true
     *
     * Keycloak:
     *   principal.roles includes "admin"
     */
    console.log('Principal roles:', roles);
    const isAdmin =
      Boolean(principal?.is_admin) ||
      roles.includes('admin');

    /*
     * A human user is considered signed in when /auth/me
     * identifies the principal as human.
     *
     * Keep getSessionToken() for compatibility with the
     * existing session implementation.
     */
    const signedIn = Boolean(
      principal?.is_human &&
      getSessionToken(),
    );

    /*
     * Role helper used throughout the UI.
     *
     * Administrators automatically satisfy all role checks.
     */
    const hasRole = (role) => {
      if (!role) {
        return false;
      }

      return isAdmin || roles.includes(role);
    };

    return {
      principal,
      loading,
      refresh,
      signedIn,
      isAdmin,
      hasRole,
    };
  }, [principal, loading, refresh]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;